import {
   For,
   Show,
   createEffect,
   createMemo,
   createSignal,
   onCleanup,
   onMount,
} from 'solid-js'

import { useI18n } from '../../i18n'
import {
   type FastRange,
   historyTimeBlocksDetail,
   normalizeRange,
   normalizeRanges,
} from '../../state/historyTimeBlocksStore'
import { createLiveClock } from '../../utils/liveClock'

type CellState = 'nonfast' | 'first8h' | 'deep'

const HOUR_IN_MS = 60 * 60 * 1000
const DAY_IN_MS = 24 * HOUR_IN_MS
const EIGHT_HOURS_IN_MS = 8 * HOUR_IN_MS
const LIVE_TICK_INTERVAL_MS = 60 * 1000

/**
 * Returns whether the viewport is narrow (mobile layout).
 *
 * @returns True on a narrow viewport.
 */
function prefersNarrowViewport(): boolean {
   if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true
   }

   return window.matchMedia('(max-width: 639px)').matches
}

/**
 * Subscribes to narrow-viewport media-query changes.
 *
 * @param callback - Invoked with the current narrow state.
 * @returns A cleanup function.
 */
function attachViewportListener(callback: (isNarrow: boolean) => void) {
   if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return
   }

   const media = window.matchMedia('(max-width: 639px)')
   /**
    * Forwards a media-query change to the callback.
    *
    * @param event - The media-query change event.
    */
   const handler = (event: MediaQueryListEvent) => callback(event.matches)

   if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', handler)
   } else if (typeof media.addListener === 'function') {
      media.addListener(handler)
   }

   callback(media.matches)

   onCleanup(() => {
      if (typeof media.removeEventListener === 'function') {
         media.removeEventListener('change', handler)
      } else if (typeof media.removeListener === 'function') {
         media.removeListener(handler)
      }
   })
}

/**
 * Returns the grid layout config for a period and viewport width.
 *
 * @param periodKey - The period (`week`/`month`).
 * @param narrow - Whether the viewport is narrow.
 * @param translate - The translation function.
 * @returns The grid config.
 */
function getVisualizationConfig(
   periodKey: 'week' | 'month',
   narrow: boolean,
   translate: (key: string) => string,
) {
   if (periodKey === 'week') {
      return narrow ?
            {
               columns: 14,
               rows: 6,
               days: 7,
               blockLabel: translate('2-hour blocks'),
            }
         :  {
               columns: 42,
               rows: 4,
               days: 7,
               blockLabel: translate('1-hour blocks'),
            }
   }

   return narrow ?
         {
            columns: 15,
            rows: 6,
            days: 30,
            blockLabel: translate('8-hour blocks'),
         }
      :  {
            columns: 45,
            rows: 4,
            days: 30,
            blockLabel: translate('4-hour blocks'),
         }
}

/**
 * Merges the in-progress fast into the sorted history ranges, extending it to
 * `referenceNow` so the live block keeps growing between store updates.
 *
 * @param sortedRanges - History ranges sorted by start.
 * @param currentFastRange - The in-progress fast range, or null.
 * @param referenceNow - The current time.
 * @returns The merged, start-sorted ranges.
 */
function mergeCurrentFastRange(
   sortedRanges: FastRange[],
   currentFastRange: FastRange | null,
   referenceNow: number,
) {
   if (!currentFastRange) {
      return sortedRanges
   }
   const extendedCurrentRange = {
      start: currentFastRange.start,
      end: Math.max(referenceNow, currentFastRange.end),
   }
   let replaced = false
   const ranges = sortedRanges.map(range => {
      if (
         !replaced &&
         range.start === currentFastRange.start &&
         range.end === currentFastRange.end
      ) {
         replaced = true
         return extendedCurrentRange
      }
      return range
   })
   if (!replaced) {
      ranges.push(extendedCurrentRange)
   }
   return ranges.sort((a, b) => a.start - b.start)
}

/**
 * Sums how much of one block falls inside fasts, split into the first eight
 * hours of each fast and the deeper phase after that.
 *
 * @param ranges - Start-sorted fast ranges.
 * @param startIndex - Index of the first range that may overlap the block.
 * @param blockStart - Block start time.
 * @param blockEnd - Block end time.
 * @returns Fasting milliseconds in the block by phase.
 */
function getBlockFastingMs(
   ranges: FastRange[],
   startIndex: number,
   blockStart: number,
   blockEnd: number,
) {
   let firstMs = 0
   let deepMs = 0
   for (let j = startIndex; j < ranges.length; j += 1) {
      const fast = ranges[j]
      if (!fast) {
         continue
      }
      if (fast.start >= blockEnd) {
         break
      }
      const overlapStart = Math.max(blockStart, fast.start)
      const overlapEnd = Math.min(blockEnd, fast.end)
      if (overlapEnd <= overlapStart) {
         continue
      }
      const firstPhaseEnd = Math.min(fast.start + EIGHT_HOURS_IN_MS, fast.end)
      const firstOverlapEnd = Math.min(overlapEnd, firstPhaseEnd)
      if (firstOverlapEnd > overlapStart) {
         firstMs += firstOverlapEnd - overlapStart
      }
      const deepOverlapStart = Math.max(overlapStart, firstPhaseEnd)
      if (overlapEnd > deepOverlapStart) {
         deepMs += overlapEnd - deepOverlapStart
      }
   }
   const cappedFirst = Math.min(firstMs, blockEnd - blockStart)
   const cappedDeep = Math.min(deepMs, Math.max(0, blockEnd - blockStart - cappedFirst))
   return { firstMs: cappedFirst, deepMs: cappedDeep }
}

/**
 * Picks a block's colour from its fasting phase totals.
 *
 * @param firstMs - Milliseconds in the first eight hours of a fast.
 * @param deepMs - Milliseconds beyond the first eight hours.
 * @returns The cell state.
 */
function getCellState(firstMs: number, deepMs: number): CellState {
   if (deepMs > 0) {
      return 'deep'
   }
   return firstMs > 0 ? 'first8h' : 'nonfast'
}

/**
 * Buckets the period ending at `rangeEnd` into equal blocks and classifies
 * each by its dominant fasting phase, accumulating hour totals.
 *
 * @param ranges - Start-sorted fast ranges.
 * @param totalCells - Number of blocks.
 * @param totalPeriodMs - Length of the whole period.
 * @param rangeEnd - End of the period (now).
 * @returns Cell states and rounded hour totals per phase.
 */
function buildTimeBlockCells(
   ranges: FastRange[],
   totalCells: number,
   totalPeriodMs: number,
   rangeEnd: number,
) {
   const rangeStart = rangeEnd - totalPeriodMs
   const blockDuration = totalPeriodMs / totalCells
   let totalFirstMs = 0
   let totalDeepMs = 0
   let totalNonfastMs = 0
   const cells: CellState[] = []
   let activeIndex = 0

   for (let index = 0; index < totalCells; index += 1) {
      const blockStart = rangeStart + index * blockDuration
      const blockEnd = blockStart + blockDuration
      while (
         activeIndex < ranges.length &&
         (ranges[activeIndex]?.end ?? Number.POSITIVE_INFINITY) <= blockStart
      ) {
         activeIndex += 1
      }
      const { firstMs, deepMs } = getBlockFastingMs(
         ranges,
         activeIndex,
         blockStart,
         blockEnd,
      )
      totalFirstMs += firstMs
      totalDeepMs += deepMs
      totalNonfastMs += Math.max(0, blockDuration - firstMs - deepMs)
      cells.push(getCellState(firstMs, deepMs))
   }

   return {
      cells,
      nonfastHours: Math.round(totalNonfastMs / HOUR_IN_MS),
      firstHours: Math.round(totalFirstMs / HOUR_IN_MS),
      deepHours: Math.round(totalDeepMs / HOUR_IN_MS),
   }
}

/**
 * Renders the phase legend with hour totals under the grid.
 *
 * @param props - Hour totals per phase.
 * @returns The legend element.
 */
const HistoryTimeBlockLegend = (props: {
   nonfastHours: number
   firstHours: number
   deepHours: number
}) => {
   const t = useI18n()
   const entries = createMemo(() => [
      {
         state: 'nonfast',
         label: t('Outside fast · {hours}h', { hours: props.nonfastHours }),
      },
      { state: 'first8h', label: t('0-8h · {hours}h', { hours: props.firstHours }) },
      { state: 'deep', label: t('8h+ · {hours}h', { hours: props.deepHours }) },
   ])
   return (
      <div class="flex flex-wrap items-center gap-3 mt-2 text-[11px] text-emerald-100/70">
         <For each={entries()}>
            {entry => (
               <span class="flex items-center gap-1">
                  <span class="history-time-block-legend" data-state={entry.state} />
                  <span>{entry.label}</span>
               </span>
            )}
         </For>
      </div>
   )
}

/**
 * Renders past fasts as a grid of time blocks for the selected period.
 *
 * @returns The time-blocks element.
 */
const HistoryTimeBlocks = () => {
   const t = useI18n()
   const [isNarrow, setIsNarrow] = createSignal(prefersNarrowViewport())
   const [now, refreshNow] = createLiveClock({ intervalMs: LIVE_TICK_INTERVAL_MS })
   onMount(() => {
      attachViewportListener(setIsNarrow)
   })

   createEffect(() => {
      historyTimeBlocksDetail()
      refreshNow()
   })

   const visualization = createMemo(() => {
      const currentDetail = historyTimeBlocksDetail()
      const referenceNow = now()
      const periodKey = currentDetail.periodKey === 'week' ? 'week' : 'month'
      const ranges = mergeCurrentFastRange(
         normalizeRanges(currentDetail.ranges).sort((a, b) => a.start - b.start),
         normalizeRange(currentDetail.currentFastRange),
         referenceNow,
      )
      const config = getVisualizationConfig(periodKey, isNarrow(), t)
      return {
         periodLabel: currentDetail.periodLabel,
         blockLabel: config.blockLabel,
         columns: config.columns,
         rows: config.rows,
         ...buildTimeBlockCells(
            ranges,
            config.columns * config.rows,
            config.days * DAY_IN_MS,
            referenceNow,
         ),
      }
   })

   return (
      <div
         class="history-time-blocks"
         aria-live="polite"
         data-period={historyTimeBlocksDetail().periodKey}
      >
         <Show
            when={visualization()}
            fallback={
               <span class="text-xs text-emerald-100/60">
                  {t('Preparing visualization…')}
               </span>
            }
         >
            {viz => (
               <>
                  <div
                     class="history-time-block-grid"
                     role="grid"
                     aria-label={`${viz().periodLabel} · ${viz().blockLabel}`}
                     style={{
                        'grid-template-columns': `repeat(${viz().columns}, minmax(0, 1fr))`,
                        'grid-template-rows': `repeat(${viz().rows}, minmax(0, 1fr))`,
                     }}
                  >
                     <For each={viz().cells}>
                        {state => (
                           <span
                              class="history-time-block-cell"
                              data-state={state}
                              aria-hidden="true"
                           />
                        )}
                     </For>
                  </div>
                  <HistoryTimeBlockLegend
                     nonfastHours={viz().nonfastHours}
                     firstHours={viz().firstHours}
                     deepHours={viz().deepHours}
                  />
               </>
            )}
         </Show>
      </div>
   )
}

export default HistoryTimeBlocks
